import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useWidgetSDK } from '../../../../widgets';
import ToothDiagnosisExtras from '../extensions/ToothDiagnosisExtras';
import {
  composeContentWithSurfaces,
  parseSurfacesFromContent,
} from '../../../../components/procedures/widget/dental/parseSurfacesFromContent';

/**
 * TabletToothActionPopover — Variant M tablet entry point for adding a
 * diagnosis or procedure on a selected tooth.
 *
 * Mounted inside the chart's `archHost` (which is `position: relative`)
 * as an absolute-positioned card anchored next to the first selected
 * tooth's `<g data-tooth-fdi="...">`. Replaces the side-column
 * `ToothActionsPanel` in `presentation="tabletPaired"` so the chart
 * pane never resizes on tooth-tap.
 *
 * The SDK calls (`sdk.openWidget('diagnosis', ...)` and
 * `sdk.openWidget('procedure', ...)`) mirror the production callers in
 * `ToothActionsPanel` 1:1 — same context shape, same opts (extras /
 * composePayload for diagnosis, placement: 'chart-pane' + requestId for
 * procedure). The chart's existing `arogya:widgets:*-saved` listeners
 * upstream handle optimistic insert.
 *
 * Props:
 *   visit              — visit row (visit_id required)
 *   viewer             — { memberId, role }
 *   selectedTokens     — string[] of FDI tokens (raw, may carry gum: prefix
 *                        + surface suffix; passed straight through to SDK)
 *   notesByToken       — Map<token, NoteEntry[]> from usePatientToothHistory
 *                        (used to find the latest diagnosis for procedure prefill)
 *   displayMarkers     — Map<token, Marker> for the optional summary line
 *   isDark             — boolean
 *   archHostRef        — ref to the chart-column container that wraps the
 *                        Odontogram (must be position: relative)
 *   onClose            — () => void; called when user taps ✕ or clicks
 *                        outside; the parent typically clears
 *                        `selectedTokens`
 */
const POP_W = 220;
const POP_H = 180;
const HIT_GAP = 12;

function _primaryToken(tokens) {
  return tokens && tokens.length > 0 ? tokens[0] : null;
}

function _splitSurface(token) {
  if (!token) return { tooth: null, surface: null };
  const stripped = typeof token === 'string' && token.startsWith('gum:') ? token.slice(4) : token;
  const m = /^([0-9A-Z]+)-([MDBLO])$/.exec(stripped);
  if (m) return { tooth: m[1], surface: m[2] };
  return { tooth: stripped, surface: null };
}

function _stripPrefix(token) {
  if (!token) return '';
  return typeof token === 'string' && token.startsWith('gum:') ? token.slice(4) : token;
}

function _toothLabel(token) {
  const { tooth, surface } = _splitSurface(token);
  if (!tooth) return '';
  if (/^Q[1-4]$/.test(tooth)) return `Quadrant ${tooth.slice(1)}`;
  if (tooth === 'FM') return 'Full mouth';
  return surface ? `Tooth #${tooth} (${surface})` : `Tooth #${tooth}`;
}

export default function TabletToothActionPopover({
  visit,
  viewer,
  selectedTokens,
  notesByToken,
  displayMarkers,
  isDark,
  archHostRef,
  onClose,
}) {
  const sdk = useWidgetSDK();
  const popoverRef = useRef(null);
  const [anchor, setAnchor] = useState({ top: 0, left: 0, ready: false });

  const primary = _primaryToken(selectedTokens);
  const { tooth: primaryToothFdi } = _splitSurface(primary || '');
  const headerLabel = (() => {
    if (!selectedTokens || selectedTokens.length === 0) return 'No selection';
    if (selectedTokens.length === 1) return _toothLabel(primary);
    const labels = selectedTokens.map(_toothLabel).join(', ');
    return `${selectedTokens.length} sites: ${labels}`;
  })();

  // Compute anchor position relative to the archHost — query the tooth's
  // `<g data-tooth-fdi>` element and offset by its bounding box.
  // Re-runs whenever the selected tooth changes (or the popover mounts).
  useLayoutEffect(() => {
    if (!archHostRef?.current || !primaryToothFdi) return;
    const hostEl = archHostRef.current;
    const toothEl = hostEl.querySelector(`[data-tooth-fdi="${primaryToothFdi}"]`);
    if (!toothEl) return;
    const hostBox = hostEl.getBoundingClientRect();
    const tBox = toothEl.getBoundingClientRect();
    // Default: place to the right of the tooth, vertically near its top.
    let left = tBox.right - hostBox.left + HIT_GAP;
    let top = tBox.top - hostBox.top - 8;
    // Overflow-correct against archHost bounds.
    if (left + POP_W > hostBox.width - 8) {
      // Not enough room on the right — flip to the left of the tooth.
      left = tBox.left - hostBox.left - POP_W - HIT_GAP;
    }
    if (left < 8) left = 8;                       // never bleed off the left edge
    if (top + POP_H > hostBox.height - 8) top = hostBox.height - POP_H - 8;
    if (top < 8) top = 8;
    setAnchor({ top, left, ready: true });
  }, [archHostRef, primaryToothFdi, selectedTokens?.length]);

  // Click-outside dismiss. The chart's tooth hit-rects live INSIDE the
  // archHost; if the user taps another tooth we want the upstream
  // `handleToothClick` to update `selectedTokens` (and our anchor effect
  // re-runs) — so we only close on clicks that are neither inside the
  // popover NOR inside the SVG (taps on background slabs, etc.).
  useEffect(() => {
    if (!anchor.ready) return undefined;
    const onDocPointer = (e) => {
      const popEl = popoverRef.current;
      if (popEl && popEl.contains(e.target)) return;
      if (e.target?.closest?.('[data-tooth-fdi]')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDocPointer, true);
    document.addEventListener('touchstart', onDocPointer, true);
    return () => {
      document.removeEventListener('mousedown', onDocPointer, true);
      document.removeEventListener('touchstart', onDocPointer, true);
    };
  }, [anchor.ready, onClose]);

  // ── Diagnosis add — mirrors ToothActionsPanel.openDiagnosisWidget ──
  // Only single-tooth tokens (not gum / quadrant / FM) accept surface edit.
  const allowSurfaceEdit = selectedTokens.length === 1
    && primary
    && !primary.startsWith('gum:')
    && !/^Q[1-4]$|^FM$/.test(_stripPrefix(primary));

  const handleAddDiagnosis = useCallback(() => {
    if (!visit?.visit_id || selectedTokens.length === 0) return;
    sdk.openWidget(
      'diagnosis',
      {
        visitId: visit.visit_id,
        anatomicalRef: {
          kind: 'dental-tooth-surface',
          payload: { tokens: selectedTokens },
          summary: headerLabel,
        },
      },
      {
        source: 'chart',
        extras: <ToothDiagnosisExtras showSurfaces={allowSurfaceEdit} />,
        composePayload: ({ content, severity, surfaces }) => {
          const trimmed = String(content || '').trim();
          const surfList = Array.isArray(surfaces) ? surfaces : [];
          const composed = allowSurfaceEdit && surfList.length > 0
            ? composeContentWithSurfaces(surfList, trimmed)
            : trimmed;
          return { content: composed, severity };
        },
      },
    );
    onClose?.();
  }, [sdk, visit, selectedTokens, headerLabel, allowSurfaceEdit, onClose]);

  // ── Procedure add — mirrors ToothActionsPanel.planProcedureFor ──
  // Links to the latest diagnosis on the primary tooth if one exists.
  const handleAddProcedure = useCallback(() => {
    if (!visit?.visit_id || selectedTokens.length === 0) return;
    // Find the latest diagnosis on the primary token to seed the prefill.
    let latestDiagnosis = null;
    if (primary && notesByToken) {
      const notes = notesByToken.get(primary) || notesByToken.get(_stripPrefix(primary)) || [];
      latestDiagnosis = notes.find((n) => String(n?.kind || '').toLowerCase() === 'diagnosis') || null;
    }
    const parsed = latestDiagnosis ? parseSurfacesFromContent(latestDiagnosis.content || '') : { rest: '' };
    const baseSummary = latestDiagnosis ? (parsed.rest || latestDiagnosis.content) : null;

    const toothFdis = selectedTokens
      .map((t) => _splitSurface(t).tooth)
      .filter((t) => t && !/^Q[1-4]$|^FM$/.test(t))
      .filter((t, i, a) => a.indexOf(t) === i)
      .sort();
    const toothLabel = toothFdis.length === 0
      ? ''
      : toothFdis.length === 1 ? `Tooth #${toothFdis[0]}` : `Teeth #${toothFdis.join(', #')}`;
    const composedSummary = toothLabel && baseSummary
      ? `${toothLabel} · ${baseSummary}`
      : (toothLabel || baseSummary || null);

    const requestId = (
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    );
    const prefill = {
      diagnosisMetaId: latestDiagnosis?.meta_id || null,
      tokens: selectedTokens,
      summary: composedSummary,
      procedureId: null,
      procedureName: null,
      requestId,
    };

    sdk.openWidget(
      'procedure',
      {
        visitId: visit.visit_id,
        anatomicalRef: {
          kind: 'dental-tooth-surface',
          payload: { tokens: selectedTokens },
          summary: headerLabel,
        },
        prefill,
      },
      {
        source: 'chart',
        // Chart-pane placement — ToothChartContainer's tabletPaired mode
        // mounts ChartPaneProcedurePicker as a centered modal (vs the
        // legacy side-column placement on desktop).
        placement: 'chart-pane',
        requestId,
      },
    );
    onClose?.();
  }, [sdk, visit, selectedTokens, primary, notesByToken, headerLabel, onClose]);

  if (!primary) return null;

  const markerSummary = (() => {
    if (!displayMarkers || !primary) return null;
    const m = displayMarkers.get(primary) || displayMarkers.get(_stripPrefix(primary));
    if (!m) return null;
    return m.summary || (m.kind ? String(m.kind).replace(/_/g, ' ') : null);
  })();

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Tooth action picker"
      data-keep-bubble-open="1"
      className={[
        'absolute z-30 rounded-xl shadow-2xl ring-1 p-2 select-none',
        isDark
          ? 'bg-slate-900 ring-teal-700 text-slate-100'
          : 'bg-white ring-teal-300 text-slate-900',
        anchor.ready ? 'opacity-100' : 'opacity-0 pointer-events-none',
      ].join(' ')}
      style={{
        top: anchor.top,
        left: anchor.left,
        width: POP_W,
        transition: 'opacity 120ms ease',
      }}
    >
      <div className="flex items-center justify-between px-1 pb-1.5 border-b border-slate-200 dark:border-slate-700">
        <strong className="text-[13px]">{headerLabel}</strong>
        <button
          type="button"
          onClick={() => onClose?.()}
          className={[
            'w-7 h-7 rounded-md flex items-center justify-center text-sm',
            isDark
              ? 'text-slate-400 hover:bg-slate-800'
              : 'text-slate-500 hover:bg-slate-100',
          ].join(' ')}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {markerSummary && (
        <div className={[
          'text-[11px] mt-1.5 px-1',
          isDark ? 'text-slate-300' : 'text-slate-700',
        ].join(' ')}>
          {markerSummary}
        </div>
      )}

      <div className="flex flex-col gap-1.5 mt-2">
        <button
          type="button"
          onClick={handleAddDiagnosis}
          className={[
            'flex items-center gap-2 rounded-md px-2.5 py-2.5 text-[13px] font-semibold',
            'bg-rose-600 hover:bg-rose-700 text-white',
            'focus:outline-none focus:ring-2 focus:ring-rose-300',
          ].join(' ')}
        >
          <span aria-hidden="true">🩺</span>
          <span>+ Add diagnosis</span>
        </button>
        <button
          type="button"
          onClick={handleAddProcedure}
          className={[
            'flex items-center gap-2 rounded-md px-2.5 py-2.5 text-[13px] font-semibold',
            'bg-teal-600 hover:bg-teal-700 text-white',
            'focus:outline-none focus:ring-2 focus:ring-teal-300',
          ].join(' ')}
        >
          <span aria-hidden="true">＋</span>
          <span>+ Add procedure</span>
        </button>
      </div>
    </div>
  );
}
